import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, BellDot, CalendarClock, CheckCircle2, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { useCapabilities } from "@/lib/capabilities";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** Real attention feed — open recommendations, critical/high findings and the
 * latest posture digest. Nothing decorative: the dot only lights up when there
 * is genuinely something waiting. */
export function AttentionMenu() {
    const nav = useNavigate();
    const { caps } = useCapabilities();
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/stats/overview");
                const sev = data.severity_counts || {};
                const next = [];
                if (data.open_recommendations > 0) {
                    next.push({
                        id: "open-recs",
                        icon: Lightbulb,
                        tone: "text-amber-400",
                        title: `${data.open_recommendations} recommendation${data.open_recommendations === 1 ? "" : "s"} awaiting review`,
                        sub: "Approve or reject to close the loop",
                        to: "/recommendations?status=open",
                    });
                }
                if ((sev.critical || 0) > 0) {
                    next.push({
                        id: "critical",
                        icon: AlertTriangle,
                        tone: "text-rose-400",
                        title: `${sev.critical} critical finding${sev.critical === 1 ? "" : "s"} open`,
                        sub: "Highest-severity authorization exposure",
                        to: "/findings?severity=critical",
                    });
                }
                if ((sev.high || 0) > 0) {
                    next.push({
                        id: "high",
                        icon: AlertTriangle,
                        tone: "text-orange-400",
                        title: `${sev.high} high-severity finding${sev.high === 1 ? "" : "s"}`,
                        sub: "Review before the next transport",
                        to: "/findings?severity=high",
                    });
                }
                if (caps?.is_admin) {
                    const dg = await api.get("/admin/digests", { params: { limit: 1 } }).catch(() => null);
                    const latest = dg?.data?.digests?.[0];
                    const sched = dg?.data?.schedule;
                    if (latest) {
                        next.push({
                            id: "digest",
                            icon: CalendarClock,
                            tone: "text-cyan-400",
                            title: `Posture digest · ${latest.label || latest.period_label || "latest"}`,
                            sub: sched?.next_run_at ? `Next run ${new Date(sched.next_run_at).toUTCString().slice(5, 22)} UTC` : "Weekly bundle ready",
                            to: "/admin/digest",
                        });
                    }
                }
                setItems(next);
            } finally {
                setLoaded(true);
            }
        })();
    }, [caps]);

    const hasAttention = items.length > 0;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="topbar-attention-button"
                    className="relative p-2 panel hover:border-cyan-500/30 transition-colors"
                    aria-label={hasAttention ? `${items.length} items need attention` : "Nothing needs attention"}
                    title="Needs attention"
                >
                    <BellDot size={16} className="text-slate-400" />
                    {hasAttention && (
                        <span
                            data-testid="topbar-attention-dot"
                            className="absolute -top-1 -right-1 dot"
                            style={{ background: "var(--warning)", boxShadow: "0 0 8px var(--warning)" }}
                        />
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                data-testid="topbar-attention-panel"
                className="bg-[var(--bg-elevated)] border-white/10 text-slate-200 w-[340px] p-0"
            >
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wider text-slate-500 px-4 pt-3">
                    Needs attention
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/5" />
                {!loaded && <div className="px-4 py-6 text-xs font-mono text-slate-500">Reading posture…</div>}
                {loaded && !hasAttention && (
                    <div className="px-4 py-6 flex items-start gap-3" data-testid="topbar-attention-empty">
                        <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                        <div>
                            <div className="text-[13px] text-white">All clear</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                No open recommendations and no critical findings.
                            </div>
                        </div>
                    </div>
                )}
                {items.map((it) => (
                    <button
                        key={it.id}
                        data-testid={`topbar-attention-item-${it.id}`}
                        onClick={() => nav(it.to)}
                        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition-colors border-b border-white/5 last:border-0"
                    >
                        <it.icon size={15} className={`${it.tone} mt-0.5 shrink-0`} />
                        <span className="min-w-0">
                            <span className="block text-[13px] text-white leading-snug">{it.title}</span>
                            <span className="block text-[11px] text-slate-500 mt-0.5">{it.sub}</span>
                        </span>
                    </button>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
